import { c as _c } from "react/compiler-runtime";
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { archiveNegociacaoCaso, closeNegociacaoCaso, exportNegociacaoCasosCsv, fetchNegociacaoCasos, pauseNegociacaoCaso, resumeNegociacaoCaso, selectNegociacaoCasos, selectNegociacaoCasosError, selectNegociacaoCasosLoading, selectNegociacaoCasosPagination, selectNegociacaoExportingCsv, selectNegociacaoTabCounts, selectNegociacoesListUi, selectNegociacoesSituacoes, setListUiActiveTab, setListUiFilters, setListUiPage, setListUiSearchQuery, setListUiSortConfig, unarchiveNegociacaoCaso } from '@/features/negociacoes/store';
import { bulkEncerrarNegociacaoCasos, cancelNegociacaoStartCaso, encerrarNegociacaoCaso } from '@/features/negociacoes/store/actions';
import type { FetchNegociacaoCasosInput } from '@/features/negociacoes/store/actions';
import type { PeriodoValor } from '@/shared/types/filtroPeriodo';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { toast } from 'sonner';
import type { CloseNegociacaoCasoResult, TipoEncerramento } from '../types';
import type { NegociacaoListRow, NegociacoesActiveChip, NegociacoesFilterCategory, NegociacoesFilters, NegociacoesSortConfig, NegociacoesSortKey, NegociacoesTabValue } from '../types/negociacoesUiTypes';
import type { NegociacaoPeriodoCampo } from '../types';
import { buildPaginationRange, legendaDoPeriodo, PERIODO_NEGOCIACAO_VAZIO, rotuloDoChipPeriodo, toNegociacaoListRow } from '../utils';
import { cadenciaDasTemperaturas, montarFiltrosDaLista, pendenciaDoFiltro, situacoesDoFiltro, statusDaAbaTodos } from '../utils/filtrosDaListaParaApi';
import { useNegociacoesPeriodo } from './useNegociacoesPeriodo';
const tabLabelMap: Record<Exclude<NegociacoesTabValue, 'ALL'>, string> = {
  PENDING: 'Com pendência',
  ARCHIVED: 'Arquivadas'
};
const defaultFilters: NegociacoesFilters = {
  status: [],
  temperature: [],
  pendency: [],
  situacao: []
};
interface UseNegociacoesPageReturn {
  activeTab: NegociacoesTabValue;
  setActiveTab: (value: NegociacoesTabValue) => void;
  page: number;
  setPage: (value: number) => void;
  limit: number;
  isLoading: boolean;
  error: string | null;
  totalResults: number;
  totalPages: number;
  paginationText: string;
  searchInput: string;
  setSearchInput: (value: string) => void;
  searchQuery: string;
  filters: NegociacoesFilters;
  setFilters: Dispatch<SetStateAction<NegociacoesFilters>>;
  tempFilters: NegociacoesFilters;
  setTempFilters: Dispatch<SetStateAction<NegociacoesFilters>>;
  isDrawerOpen: boolean;
  setIsDrawerOpen: (value: boolean) => void;
  hasActiveFilters: boolean;
  hasTempFilters: boolean;
  periodo: PeriodoValor;
  campoPeriodoAtivo: NegociacaoPeriodoCampo | null;
  legendaPeriodo: string;
  aplicarPeriodo: (valor: PeriodoValor) => void;
  atualizarRascunhoPeriodo: (valor: PeriodoValor | null) => void;
  resumoPreviaPeriodo: string | undefined;
  tempPeriodo: PeriodoValor;
  setTempPeriodo: Dispatch<SetStateAction<PeriodoValor>>;
  activeChips: NegociacoesActiveChip[];
  sortConfig: NegociacoesSortConfig;
  handleSort: (key: NegociacoesSortKey) => void;
  selectedIds: Set<string>;
  toggleSelectAll: () => void;
  toggleSelect: (id: string) => void;
  clearSelection: () => void;
  pauseCaso: (caseId: string) => Promise<void>;
  resumeCaso: (caseId: string) => Promise<void>;
  closeCaso: (caseId: string, resultStatus: CloseNegociacaoCasoResult['resultStatus']) => Promise<boolean>;
  encerrarCaso: (caseId: string, tipo: TipoEncerramento, observacoes: string, detalhesAcordo?: {
    valorAcordado: number;
    parcelas?: number;
    primeiroVencimento?: string;
  }) => Promise<boolean>;
  archiveCaso: (caseId: string) => Promise<void>;
  unarchiveCaso: (caseId: string) => Promise<void>;
  cancelStartCaso: (caseId: string) => Promise<void>;
  pauseSelected: () => Promise<void>;
  resumeSelected: () => Promise<void>;
  archiveSelected: () => Promise<void>;
  unarchiveSelected: () => Promise<void>;
  closeSelected: (tipo: TipoEncerramento, observacoes: string, detalhesAcordo?: {
    valorAcordado: number;
    parcelas?: number;
    primeiroVencimento?: string;
  }) => Promise<void>;
  rows: NegociacaoListRow[];
  openDrawer: () => void;
  applyFilters: () => void;
  clearTempFilters: () => void;
  clearAllFilters: () => void;
  removeChip: (chip: NegociacoesActiveChip) => void;
  toggleFilterValue: (category: NegociacoesFilterCategory, value: string) => void;
  clearFilterCategory: (category: NegociacoesFilterCategory) => void;
  toggleTempFilterValue: (category: NegociacoesFilterCategory, value: string) => void;
  selectAllTempInCategory: (category: NegociacoesFilterCategory, options: string[]) => void;
  retryFetch: () => void;
  exportCsv: (format: 'csv' | 'xlsx') => void;
  isExportingCsv: boolean;
  kpis: {
    resultsLabel: string;
    resultsValue: string;
    needsActionLabel: string;
    needsActionValue: string;
    overdueLabel: string;
    overdueValue: string;
    concludedLabel: string;
    concludedValue: string;
  };
  hasExistingNegociacoes: boolean;
  tabCounts: {
    all: number;
    open: number;
    concluded: number;
    closed: number;
    archived: number;
    withPendency: number;
  };
}
export const useNegociacoesPage = () => {
  const $ = _c(247);
  const dispatch = useAppDispatch();
  const casos = useAppSelector(selectNegociacaoCasos);
  const isLoading = useAppSelector(selectNegociacaoCasosLoading);
  const error = useAppSelector(selectNegociacaoCasosError);
  const pagination = useAppSelector(selectNegociacaoCasosPagination);
  const isExportingCsv = useAppSelector(selectNegociacaoExportingCsv);
  const tabCounts = useAppSelector(selectNegociacaoTabCounts);
  const listUi = useAppSelector(selectNegociacoesListUi);
  const activeTab = listUi.activeTab;
  let t0;
  if ($[0] !== dispatch) {
    t0 = value => dispatch(setListUiActiveTab(value));
    $[0] = dispatch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const setActiveTab = t0;
  const page = listUi.page;
  const limit = 10;
  const [searchInput, setSearchInputLocal] = useState(listUi.searchQuery);
  const searchQuery = listUi.searchQuery;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const filters = listUi.filters;
  let t1;
  if ($[2] !== dispatch || $[3] !== listUi.filters) {
    t1 = updater => {
      if (typeof updater === "function") {
        dispatch(setListUiFilters(updater(listUi.filters)));
      } else {
        dispatch(setListUiFilters(updater));
      }
    };
    $[2] = dispatch;
    $[3] = listUi.filters;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const setFilters = t1;
  const [tempFilters, setTempFilters] = useState(listUi.filters);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const [selectedIds, setSelectedIds] = useState(t2);
  const sortConfig = listUi.sortConfig;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = value_0 => {
      setSearchInputLocal(value_0);
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const setSearchInput = t3;
  const autoRefreshedRef = useRef(false);
  const [hasExistingNegociacoes, setHasExistingNegociacoes] = useState(_temp);
  let t4;
  let t5;
  if ($[7] !== hasExistingNegociacoes || $[8] !== pagination.total) {
    t4 = () => {
      if (pagination.total > 0 && !hasExistingNegociacoes) {
        setHasExistingNegociacoes(true);
        window.localStorage.setItem("hasExistingNegociacoes", "true");
      }
    };
    t5 = [pagination.total, hasExistingNegociacoes];
    $[7] = hasExistingNegociacoes;
    $[8] = pagination.total;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  bb0: {
    if (pagination.totalPages > 0) {
      t6 = pagination.totalPages;
      break bb0;
    }
    if (pagination.total <= 0) {
      t6 = 1;
      break bb0;
    }
    const safeLimit = pagination.limit > 0 ? pagination.limit : 10;
    const computedTotalPages = Math.ceil(pagination.total / safeLimit);
    t6 = computedTotalPages > 0 ? computedTotalPages : 1;
  }
  const maxPage = t6;
  let t7;
  if ($[11] !== dispatch || $[12] !== maxPage) {
    t7 = value_1 => {
      const next = value_1 < 1 ? 1 : value_1 > maxPage ? maxPage : value_1;
      dispatch(setListUiPage(next));
    };
    $[11] = dispatch;
    $[12] = maxPage;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const setPage = t7;
  let t8;
  if ($[14] !== activeTab || $[15] !== dispatch || $[16] !== filters) {
    t8 = () => {
      if (activeTab === "ALL") {
        return;
      }
      if (filters.status.length > 0) {
        dispatch(setListUiFilters({
          ...filters,
          status: []
        }));
      }
      setTempFilters(_temp2);
    };
    $[14] = activeTab;
    $[15] = dispatch;
    $[16] = filters;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== activeTab || $[19] !== dispatch) {
    t9 = [activeTab, dispatch];
    $[18] = activeTab;
    $[19] = dispatch;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[21] !== dispatch || $[22] !== searchInput) {
    t10 = () => {
      const handle = setTimeout(() => {
        dispatch(setListUiSearchQuery(searchInput.trim()));
      }, 350);
      return () => clearTimeout(handle);
    };
    t11 = [searchInput, dispatch];
    $[21] = dispatch;
    $[22] = searchInput;
    $[23] = t10;
    $[24] = t11;
  } else {
    t10 = $[23];
    t11 = $[24];
  }
  useEffect(t10, t11);
  let t12;
  if ($[25] !== filters.temperature) {
    t12 = cadenciaDasTemperaturas(filters.temperature);
    $[25] = filters.temperature;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const cadenceBucketInFilter = t12;
  let t13;
  if ($[27] !== filters.situacao) {
    t13 = situacoesDoFiltro(filters.situacao);
    $[27] = filters.situacao;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const situacaoIdInFilter = t13;
  let t14;
  if ($[29] !== filters.pendency) {
    t14 = pendenciaDoFiltro(filters.pendency);
    $[29] = filters.pendency;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const pendencyFilter = t14;
  let t15;
  if ($[31] !== activeTab || $[32] !== filters.status) {
    t15 = statusDaAbaTodos(activeTab, filters.status);
    $[31] = activeTab;
    $[32] = filters.status;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const statusFiltersOnAllTab = t15;
  let t16;
  if ($[34] !== statusFiltersOnAllTab.humanIntervention || $[35] !== statusFiltersOnAllTab.status || $[36] !== statusFiltersOnAllTab.statusIn) {
    t16 = {
      status: statusFiltersOnAllTab.status,
      statusIn: statusFiltersOnAllTab.statusIn,
      humanIntervention: statusFiltersOnAllTab.humanIntervention
    };
    $[34] = statusFiltersOnAllTab.humanIntervention;
    $[35] = statusFiltersOnAllTab.status;
    $[36] = statusFiltersOnAllTab.statusIn;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const tabCountsFilters = t16;
  let t17;
  if ($[38] !== activeTab || $[39] !== cadenceBucketInFilter || $[40] !== pendencyFilter || $[41] !== searchQuery || $[42] !== situacaoIdInFilter || $[43] !== statusFiltersOnAllTab) {
    t17 = montarFiltrosDaLista({
      activeTab,
      search: searchQuery,
      cadenceBucketIn: cadenceBucketInFilter,
      situacaoIdIn: situacaoIdInFilter,
      pendency: pendencyFilter,
      statusDaAba: statusFiltersOnAllTab
    });
    $[38] = activeTab;
    $[39] = cadenceBucketInFilter;
    $[40] = pendencyFilter;
    $[41] = searchQuery;
    $[42] = situacaoIdInFilter;
    $[43] = statusFiltersOnAllTab;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  const filtrosDaAba = t17;
  let t18;
  if ($[45] !== filtrosDaAba) {
    t18 = {
      filtrosDaLista: filtrosDaAba
    };
    $[45] = filtrosDaAba;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  const {
    periodo,
    parametrosApi: periodoApi,
    campoAtivo: campoPeriodoAtivo,
    aplicarPeriodo,
    removerPeriodo,
    atualizarRascunho: atualizarRascunhoPeriodo,
    resumoPrevia: resumoPreviaPeriodo
  } = useNegociacoesPeriodo(t18);
  const [tempPeriodo, setTempPeriodo] = useState(PERIODO_NEGOCIACAO_VAZIO);
  let t19;
  if ($[47] !== filtrosDaAba || $[48] !== periodoApi) {
    t19 = {
      ...filtrosDaAba,
      ...periodoApi
    };
    $[47] = filtrosDaAba;
    $[48] = periodoApi;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  const filtrosComPeriodo = t19;
  const t20 = sortConfig.key === "createdAt" && campoPeriodoAtivo === "acordo" ? "dataAcordo" : sortConfig.key;
  let t21;
  if ($[50] !== filtrosComPeriodo || $[51] !== page || $[52] !== sortConfig.direction || $[53] !== t20 || $[54] !== tabCountsFilters.humanIntervention || $[55] !== tabCountsFilters.status || $[56] !== tabCountsFilters.statusIn) {
    t21 = {
      ...filtrosComPeriodo,
      tabCountsStatus: tabCountsFilters.status,
      tabCountsStatusIn: tabCountsFilters.statusIn,
      tabCountsHumanIntervention: tabCountsFilters.humanIntervention,
      page,
      limit: 10,
      sortBy: t20,
      sortOrder: sortConfig.direction
    };
    $[50] = filtrosComPeriodo;
    $[51] = page;
    $[52] = sortConfig.direction;
    $[53] = t20;
    $[54] = tabCountsFilters.humanIntervention;
    $[55] = tabCountsFilters.status;
    $[56] = tabCountsFilters.statusIn;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  const parametrosDaLista = t21;
  let t22;
  if ($[58] !== dispatch) {
    t22 = () => {
      dispatch(setListUiPage(1));
    };
    $[58] = dispatch;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== cadenceBucketInFilter || $[61] !== dispatch || $[62] !== filters.pendency || $[63] !== periodoApi || $[64] !== situacaoIdInFilter || $[65] !== statusFiltersOnAllTab) {
    t23 = [cadenceBucketInFilter, periodoApi, situacaoIdInFilter, statusFiltersOnAllTab, filters.pendency, dispatch];
    $[60] = cadenceBucketInFilter;
    $[61] = dispatch;
    $[62] = filters.pendency;
    $[63] = periodoApi;
    $[64] = situacaoIdInFilter;
    $[65] = statusFiltersOnAllTab;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  useEffect(t22, t23);
  let t24;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => {
      setSelectedIds(new Set());
    };
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== periodoApi) {
    t25 = [periodoApi];
    $[68] = periodoApi;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[70] !== dispatch || $[71] !== parametrosDaLista) {
    t26 = () => {
      dispatch(fetchNegociacaoCasos(parametrosDaLista));
    };
    t27 = [dispatch, parametrosDaLista];
    $[70] = dispatch;
    $[71] = parametrosDaLista;
    $[72] = t26;
    $[73] = t27;
  } else {
    t26 = $[72];
    t27 = $[73];
  }
  useEffect(t26, t27);
  let t28;
  let t29;
  if ($[74] !== dispatch || $[75] !== maxPage || $[76] !== page) {
    t28 = () => {
      if (page > maxPage) {
        dispatch(setListUiPage(maxPage));
      }
    };
    t29 = [maxPage, page, dispatch];
    $[74] = dispatch;
    $[75] = maxPage;
    $[76] = page;
    $[77] = t28;
    $[78] = t29;
  } else {
    t28 = $[77];
    t29 = $[78];
  }
  useEffect(t28, t29);
  const situacoesOptions = useAppSelector(selectNegociacoesSituacoes);
  let map;
  if ($[79] !== situacoesOptions) {
    map = {};
    situacoesOptions.forEach(s => {
      map[s.id] = s.label;
    });
    $[79] = situacoesOptions;
    $[80] = map;
  } else {
    map = $[80];
  }
  const situacaoLabelById = map;
  let chips;
  if ($[81] !== campoPeriodoAtivo || $[82] !== filters.pendency || $[83] !== filters.situacao || $[84] !== filters.status || $[85] !== filters.temperature || $[86] !== periodo || $[87] !== periodoApi.periodoInicio || $[88] !== situacaoLabelById) {
    chips = [];
    if (campoPeriodoAtivo) {
      chips.push({
        label: rotuloDoChipPeriodo(periodo),
        category: "periodo",
        value: periodoApi.periodoInicio ?? ""
      });
    }
    if (filters.status.length > 0) {
      filters.status.forEach(val => chips.push({
        label: `Status: ${val}`,
        category: "status",
        value: val
      }));
    }
    if (filters.temperature.length > 0) {
      filters.temperature.forEach(val_0 => chips.push({
        label: `Temp: ${val_0}`,
        category: "temperature",
        value: val_0
      }));
    }
    if (filters.pendency.length > 0) {
      filters.pendency.forEach(val_1 => chips.push({
        label: val_1,
        category: "pendency",
        value: val_1
      }));
    }
    if (filters.situacao.length > 0) {
      filters.situacao.forEach(id => chips.push({
        label: `Situação: ${situacaoLabelById[id] ?? id}`,
        category: "situacao",
        value: id
      }));
    }
    $[81] = campoPeriodoAtivo;
    $[82] = filters.pendency;
    $[83] = filters.situacao;
    $[84] = filters.status;
    $[85] = filters.temperature;
    $[86] = periodo;
    $[87] = periodoApi.periodoInicio;
    $[88] = situacaoLabelById;
    $[89] = chips;
  } else {
    chips = $[89];
  }
  const activeChips = chips;
  let t30;
  if ($[90] !== casos) {
    t30 = casos.map(toNegociacaoListRow);
    $[90] = casos;
    $[91] = t30;
  } else {
    t30 = $[91];
  }
  const rows = t30;
  const filteredAndSortedRows = rows;
  let t31;
  if ($[92] !== campoPeriodoAtivo || $[93] !== filters) {
    t31 = campoPeriodoAtivo !== null || Object.values(filters).some(_temp3);
    $[92] = campoPeriodoAtivo;
    $[93] = filters;
    $[94] = t31;
  } else {
    t31 = $[94];
  }
  const hasActiveFilters = t31;
  let t32;
  if ($[95] !== tempFilters || $[96] !== tempPeriodo.inicio) {
    t32 = Boolean(tempPeriodo.inicio) || Object.values(tempFilters).some(_temp4);
    $[95] = tempFilters;
    $[96] = tempPeriodo.inicio;
    $[97] = t32;
  } else {
    t32 = $[97];
  }
  const hasTempFilters = t32;
  let t33;
  if ($[98] !== campoPeriodoAtivo || $[99] !== periodo) {
    t33 = campoPeriodoAtivo ? legendaDoPeriodo(periodo) : "";
    $[98] = campoPeriodoAtivo;
    $[99] = periodo;
    $[100] = t33;
  } else {
    t33 = $[100];
  }
  const legendaPeriodo = t33;
  let t34;
  if ($[101] !== dispatch || $[102] !== sortConfig.direction || $[103] !== sortConfig.key) {
    t34 = key => {
      dispatch(setListUiSortConfig({
        key,
        direction: sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc"
      }));
    };
    $[101] = dispatch;
    $[102] = sortConfig.direction;
    $[103] = sortConfig.key;
    $[104] = t34;
  } else {
    t34 = $[104];
  }
  const handleSort = t34;
  let t35;
  if ($[105] !== filteredAndSortedRows) {
    t35 = () => {
      setSelectedIds(prev_0 => {
        if (prev_0.size === filteredAndSortedRows.length) {
          return new Set();
        }
        return new Set(filteredAndSortedRows.map(_temp5));
      });
    };
    $[105] = filteredAndSortedRows;
    $[106] = t35;
  } else {
    t35 = $[106];
  }
  const toggleSelectAll = t35;
  let t36;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = id_0 => {
      setSelectedIds(prev_1 => {
        const next_0 = new Set(prev_1);
        if (next_0.has(id_0)) {
          next_0.delete(id_0);
          return next_0;
        }
        next_0.add(id_0);
        return next_0;
      });
    };
    $[107] = t36;
  } else {
    t36 = $[107];
  }
  const toggleSelect = t36;
  let t37;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = () => {
      setSelectedIds(new Set());
    };
    $[108] = t37;
  } else {
    t37 = $[108];
  }
  const clearSelection = t37;
  let t38;
  if ($[109] !== dispatch) {
    t38 = async caseId => {
      const result = await dispatch(pauseNegociacaoCaso({
        caseId
      }));
      if (pauseNegociacaoCaso.fulfilled.match(result)) {
        toast.success("Negocia\xE7\xE3o pausada");
        return;
      }
      const errorMessage = typeof result.payload === "string" ? result.payload : result.error?.message || "Erro ao pausar negocia\xE7\xE3o";
      toast.error(errorMessage);
    };
    $[109] = dispatch;
    $[110] = t38;
  } else {
    t38 = $[110];
  }
  const pauseCaso = t38;
  let t39;
  if ($[111] !== dispatch) {
    t39 = async caseId_0 => {
      const result_0 = await dispatch(unarchiveNegociacaoCaso({
        caseId: caseId_0
      }));
      if (unarchiveNegociacaoCaso.fulfilled.match(result_0)) {
        toast.success("Negocia\xE7\xE3o desarquivada");
        return;
      }
      const errorMessage_0 = typeof result_0.payload === "string" ? result_0.payload : result_0.error?.message || "Erro ao desarquivar negocia\xE7\xE3o";
      toast.error(errorMessage_0);
    };
    $[111] = dispatch;
    $[112] = t39;
  } else {
    t39 = $[112];
  }
  const unarchiveCaso = t39;
  let t40;
  if ($[113] !== dispatch) {
    t40 = async caseId_1 => {
      const result_1 = await dispatch(archiveNegociacaoCaso({
        caseId: caseId_1
      }));
      if (archiveNegociacaoCaso.fulfilled.match(result_1)) {
        toast.success("Negocia\xE7\xE3o arquivada");
        return;
      }
      const errorMessage_1 = typeof result_1.payload === "string" ? result_1.payload : result_1.error?.message || "Erro ao arquivar negocia\xE7\xE3o";
      toast.error(errorMessage_1);
    };
    $[113] = dispatch;
    $[114] = t40;
  } else {
    t40 = $[114];
  }
  const archiveCaso = t40;
  let t41;
  if ($[115] !== dispatch) {
    t41 = async caseId_2 => {
      const result_2 = await dispatch(resumeNegociacaoCaso({
        caseId: caseId_2
      }));
      if (resumeNegociacaoCaso.fulfilled.match(result_2)) {
        toast.success("Negocia\xE7\xE3o reiniciada");
        return;
      }
      const errorMessage_2 = typeof result_2.payload === "string" ? result_2.payload : result_2.error?.message || "Erro ao reiniciar negocia\xE7\xE3o";
      toast.error(errorMessage_2);
    };
    $[115] = dispatch;
    $[116] = t41;
  } else {
    t41 = $[116];
  }
  const resumeCaso = t41;
  let t42;
  if ($[117] !== dispatch || $[118] !== filteredAndSortedRows || $[119] !== selectedIds) {
    t42 = async () => {
      const selected = filteredAndSortedRows.filter(n_0 => selectedIds.has(n_0.id));
      const idsToPause = selected.filter(_temp6).map(_temp7);
      if (idsToPause.length === 0) {
        toast.error("Nenhuma negocia\xE7\xE3o selecionada pode ser pausada");
        return;
      }
      const results = await Promise.all(idsToPause.map(id_1 => dispatch(pauseNegociacaoCaso({
        caseId: id_1
      }))));
      const successCount = results.filter(_temp8).length;
      const failCount = results.length - successCount;
      if (successCount > 0 && failCount === 0) {
        toast.success("Negocia\xE7\xF5es pausadas");
        clearSelection();
        return;
      }
      if (successCount > 0) {
        toast.success(`${successCount} negociações pausadas`);
        clearSelection();
      }
      if (failCount > 0) {
        toast.error(`${failCount} negociações não puderam ser pausadas`);
      }
    };
    $[117] = dispatch;
    $[118] = filteredAndSortedRows;
    $[119] = selectedIds;
    $[120] = t42;
  } else {
    t42 = $[120];
  }
  const pauseSelected = t42;
  let t43;
  if ($[121] !== dispatch || $[122] !== filteredAndSortedRows || $[123] !== selectedIds) {
    t43 = async () => {
      const selected_0 = filteredAndSortedRows.filter(n_3 => selectedIds.has(n_3.id));
      const idsToResume = selected_0.filter(_temp9).map(_temp10);
      if (idsToResume.length === 0) {
        toast.error("Nenhuma negocia\xE7\xE3o selecionada pode ser reiniciada");
        return;
      }
      const results_0 = await Promise.all(idsToResume.map(id_2 => dispatch(resumeNegociacaoCaso({
        caseId: id_2
      }))));
      const successCount_0 = results_0.filter(_temp11).length;
      const failCount_0 = results_0.length - successCount_0;
      if (successCount_0 > 0 && failCount_0 === 0) {
        toast.success("Negocia\xE7\xF5es reiniciadas");
        clearSelection();
        return;
      }
      if (successCount_0 > 0) {
        toast.success(`${successCount_0} negociações reiniciadas`);
        clearSelection();
      }
      if (failCount_0 > 0) {
        toast.error(`${failCount_0} negociações não puderam ser reiniciadas`);
      }
    };
    $[121] = dispatch;
    $[122] = filteredAndSortedRows;
    $[123] = selectedIds;
    $[124] = t43;
  } else {
    t43 = $[124];
  }
  const resumeSelected = t43;
  let t44;
  if ($[125] !== dispatch || $[126] !== filteredAndSortedRows || $[127] !== selectedIds) {
    t44 = async () => {
      const idsToArchive = filteredAndSortedRows.filter(n_6 => selectedIds.has(n_6.id)).map(_temp12);
      if (idsToArchive.length === 0) {
        toast.error("Nenhuma negocia\xE7\xE3o selecionada pode ser arquivada");
        return;
      }
      const results_1 = await Promise.all(idsToArchive.map(id_3 => dispatch(archiveNegociacaoCaso({
        caseId: id_3
      }))));
      const successCount_1 = results_1.filter(_temp13).length;
      const failCount_1 = results_1.length - successCount_1;
      if (successCount_1 > 0 && failCount_1 === 0) {
        toast.success("Negocia\xE7\xF5es arquivadas");
        clearSelection();
        return;
      }
      if (successCount_1 > 0) {
        toast.success(`${successCount_1} negociações arquivadas`);
        clearSelection();
      }
      if (failCount_1 > 0) {
        toast.error(`${failCount_1} negociações não puderam ser arquivadas`);
      }
    };
    $[125] = dispatch;
    $[126] = filteredAndSortedRows;
    $[127] = selectedIds;
    $[128] = t44;
  } else {
    t44 = $[128];
  }
  const archiveSelected = t44;
  let t45;
  if ($[129] !== dispatch || $[130] !== filteredAndSortedRows || $[131] !== selectedIds) {
    t45 = async () => {
      const idsToUnarchive = filteredAndSortedRows.filter(n_8 => selectedIds.has(n_8.id)).map(_temp14);
      if (idsToUnarchive.length === 0) {
        toast.error("Nenhuma negocia\xE7\xE3o selecionada pode ser desarquivada");
        return;
      }
      const results_2 = await Promise.all(idsToUnarchive.map(id_4 => dispatch(unarchiveNegociacaoCaso({
        caseId: id_4
      }))));
      const successCount_2 = results_2.filter(_temp15).length;
      const failCount_2 = results_2.length - successCount_2;
      if (successCount_2 > 0 && failCount_2 === 0) {
        toast.success("Negocia\xE7\xF5es desarquivadas");
        clearSelection();
        return;
      }
      if (successCount_2 > 0) {
        toast.success(`${successCount_2} negociações desarquivadas`);
        clearSelection();
      }
      if (failCount_2 > 0) {
        toast.error(`${failCount_2} negociações não puderam ser desarquivadas`);
      }
    };
    $[129] = dispatch;
    $[130] = filteredAndSortedRows;
    $[131] = selectedIds;
    $[132] = t45;
  } else {
    t45 = $[132];
  }
  const unarchiveSelected = t45;
  let t46;
  if ($[133] !== filters || $[134] !== periodo) {
    t46 = () => {
      setTempFilters(filters);
      setTempPeriodo(periodo);
      setIsDrawerOpen(true);
    };
    $[133] = filters;
    $[134] = periodo;
    $[135] = t46;
  } else {
    t46 = $[135];
  }
  const openDrawer = t46;
  let t47;
  if ($[136] !== aplicarPeriodo || $[137] !== dispatch || $[138] !== tempFilters || $[139] !== tempPeriodo) {
    t47 = () => {
      dispatch(setListUiFilters(tempFilters));
      aplicarPeriodo(tempPeriodo);
      setIsDrawerOpen(false);
    };
    $[136] = aplicarPeriodo;
    $[137] = dispatch;
    $[138] = tempFilters;
    $[139] = tempPeriodo;
    $[140] = t47;
  } else {
    t47 = $[140];
  }
  const applyFilters = t47;
  let t48;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = () => {
      setTempFilters(defaultFilters);
      setTempPeriodo(PERIODO_NEGOCIACAO_VAZIO);
    };
    $[141] = t48;
  } else {
    t48 = $[141];
  }
  const clearTempFilters = t48;
  let t49;
  if ($[142] !== dispatch || $[143] !== removerPeriodo) {
    t49 = () => {
      dispatch(setListUiFilters(defaultFilters));
      setTempFilters(defaultFilters);
      removerPeriodo();
    };
    $[142] = dispatch;
    $[143] = removerPeriodo;
    $[144] = t49;
  } else {
    t49 = $[144];
  }
  const clearAllFilters = t49;
  let t50;
  if ($[145] !== dispatch || $[146] !== filters || $[147] !== removerPeriodo) {
    t50 = chip => {
      if (chip.category === "periodo") {
        removerPeriodo();
        return;
      }
      const next_1 = filters[chip.category].filter(v_1 => v_1 !== chip.value);
      dispatch(setListUiFilters({
        ...filters,
        [chip.category]: next_1
      }));
    };
    $[145] = dispatch;
    $[146] = filters;
    $[147] = removerPeriodo;
    $[148] = t50;
  } else {
    t50 = $[148];
  }
  const removeChip = t50;
  let t51;
  if ($[149] !== dispatch || $[150] !== filters) {
    t51 = (category, value_2) => {
      const current = filters[category];
      const next_2 = current.includes(value_2) ? current.filter(v_2 => v_2 !== value_2) : [...current, value_2];
      dispatch(setListUiFilters({
        ...filters,
        [category]: next_2
      }));
    };
    $[149] = dispatch;
    $[150] = filters;
    $[151] = t51;
  } else {
    t51 = $[151];
  }
  const toggleFilterValue = t51;
  let t52;
  if ($[152] !== dispatch || $[153] !== filters) {
    t52 = category_0 => {
      dispatch(setListUiFilters({
        ...filters,
        [category_0]: []
      }));
    };
    $[152] = dispatch;
    $[153] = filters;
    $[154] = t52;
  } else {
    t52 = $[154];
  }
  const clearFilterCategory = t52;
  let t53;
  if ($[155] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = (category_1, value_3) => {
      setTempFilters(prev_2 => {
        const current_0 = prev_2[category_1];
        const next_3 = current_0.includes(value_3) ? current_0.filter(v_3 => v_3 !== value_3) : [...current_0, value_3];
        return {
          ...prev_2,
          [category_1]: next_3
        };
      });
    };
    $[155] = t53;
  } else {
    t53 = $[155];
  }
  const toggleTempFilterValue = t53;
  let t54;
  if ($[156] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = (category_2, options) => {
      setTempFilters(prev_3 => ({
        ...prev_3,
        [category_2]: options
      }));
    };
    $[156] = t54;
  } else {
    t54 = $[156];
  }
  const selectAllTempInCategory = t54;
  let t55;
  if ($[157] !== dispatch || $[158] !== parametrosDaLista) {
    t55 = () => {
      dispatch(fetchNegociacaoCasos(parametrosDaLista));
    };
    $[157] = dispatch;
    $[158] = parametrosDaLista;
    $[159] = t55;
  } else {
    t55 = $[159];
  }
  const retryFetch = t55;
  let t56;
  if ($[160] !== dispatch || $[161] !== filteredAndSortedRows || $[162] !== retryFetch || $[163] !== selectedIds) {
    t56 = async (tipo, observacoes, detalhesAcordo) => {
      const selected_1 = filteredAndSortedRows.filter(n_10 => selectedIds.has(n_10.id));
      const idsToClose = selected_1.filter(_temp16).map(_temp17);
      if (idsToClose.length === 0) {
        toast.error("Nenhuma negocia\xE7\xE3o selecionada pode ser encerrada");
        return;
      }
      const result_3 = await dispatch(bulkEncerrarNegociacaoCasos({
        caseIds: idsToClose,
        tipo,
        observacoes,
        detalhesAcordo
      }));
      if (bulkEncerrarNegociacaoCasos.fulfilled.match(result_3)) {
        const {
          successCount: successCount_3,
          failCount: failCount_3
        } = result_3.payload;
        if (successCount_3 > 0) {
          toast.success(`${successCount_3} negociações encerradas`);
          clearSelection();
          retryFetch();
        }
        if (failCount_3 > 0) {
          toast.error(`${failCount_3} negociações não puderam ser encerradas`);
        }
        return;
      }
      toast.error(typeof result_3.payload === "string" ? result_3.payload : "Erro ao encerrar negocia\xE7\xF5es");
    };
    $[160] = dispatch;
    $[161] = filteredAndSortedRows;
    $[162] = retryFetch;
    $[163] = selectedIds;
    $[164] = t56;
  } else {
    t56 = $[164];
  }
  const closeSelected = t56;
  let t57;
  if ($[165] !== dispatch || $[166] !== retryFetch) {
    t57 = async (caseId_3, resultStatus) => {
      const result_4 = await dispatch(closeNegociacaoCaso({
        caseId: caseId_3,
        resultStatus
      }));
      if (closeNegociacaoCaso.fulfilled.match(result_4)) {
        toast.success("Negocia\xE7\xE3o encerrada");
        retryFetch();
        return true;
      }
      const errorMessage_3 = typeof result_4.payload === "string" ? result_4.payload : result_4.error?.message || "Erro ao encerrar negocia\xE7\xE3o";
      toast.error(errorMessage_3);
      return false;
    };
    $[165] = dispatch;
    $[166] = retryFetch;
    $[167] = t57;
  } else {
    t57 = $[167];
  }
  const closeCaso = t57;
  let t58;
  if ($[168] !== dispatch || $[169] !== retryFetch) {
    t58 = async (caseId_4, tipo_0, observacoes_0, detalhesAcordo_0) => {
      const result_5 = await dispatch(encerrarNegociacaoCaso({
        caseId: caseId_4,
        tipo: tipo_0,
        observacoes: observacoes_0,
        detalhesAcordo: detalhesAcordo_0,
        encerrarAtendimentoHumano: true
      }));
      if (encerrarNegociacaoCaso.fulfilled.match(result_5)) {
        toast.success("Negocia\xE7\xE3o encerrada");
        retryFetch();
        return true;
      }
      const errorMessage_4 = typeof result_5.payload === "string" ? result_5.payload : result_5.error?.message || "Erro ao encerrar negocia\xE7\xE3o";
      toast.error(errorMessage_4);
      return false;
    };
    $[168] = dispatch;
    $[169] = retryFetch;
    $[170] = t58;
  } else {
    t58 = $[170];
  }
  const encerrarCaso = t58;
  let t59;
  if ($[171] !== dispatch || $[172] !== retryFetch) {
    t59 = async caseId_5 => {
      const result_6 = await dispatch(cancelNegociacaoStartCaso({
        caseId: caseId_5
      }));
      if (cancelNegociacaoStartCaso.fulfilled.match(result_6)) {
        toast.success("Agendamento cancelado");
        retryFetch();
        return;
      }
      const errorMessage_5 = typeof result_6.payload === "string" ? result_6.payload : result_6.error?.message || "Erro ao cancelar agendamento";
      toast.error(errorMessage_5);
    };
    $[171] = dispatch;
    $[172] = retryFetch;
    $[173] = t59;
  } else {
    t59 = $[173];
  }
  const cancelStartCaso = t59;
  let t60;
  let t61;
  if ($[174] !== retryFetch) {
    t60 = () => {
      if (autoRefreshedRef.current) {
        return;
      }
      if (typeof window === "undefined") {
        return;
      }
      const shouldRefresh = window.sessionStorage.getItem("negociacoesShouldRefresh") === "1";
      if (!shouldRefresh) {
        return;
      }
      autoRefreshedRef.current = true;
      window.sessionStorage.removeItem("negociacoesShouldRefresh");
      retryFetch();
      const handle_0 = window.setTimeout(() => {
        retryFetch();
      }, 1200);
      return () => {
        window.clearTimeout(handle_0);
      };
    };
    t61 = [retryFetch];
    $[174] = retryFetch;
    $[175] = t60;
    $[176] = t61;
  } else {
    t60 = $[175];
    t61 = $[176];
  }
  useEffect(t60, t61);
  let t62;
  if ($[177] !== dispatch || $[178] !== filtrosComPeriodo) {
    t62 = format => {
      (async () => {
        const result_7 = await dispatch(exportNegociacaoCasosCsv({
          ...filtrosComPeriodo,
          format
        }));
        if (exportNegociacaoCasosCsv.fulfilled.match(result_7)) {
          if (result_7.payload.truncated) {
            toast.warning("Exporta\xE7\xE3o parcial: limite m\xE1ximo de linhas atingido. Refine os filtros para incluir todos os registros.");
          } else {
            toast.success(`${format === "xlsx" ? "Excel" : "CSV"} exportado com sucesso`);
          }
          return;
        }
        const errorMessage_6 = typeof result_7.payload === "string" ? result_7.payload : result_7.error?.message || "Erro ao exportar CSV";
        toast.error(errorMessage_6);
      })();
    };
    $[177] = dispatch;
    $[178] = filtrosComPeriodo;
    $[179] = t62;
  } else {
    t62 = $[179];
  }
  const exportCsv = t62;
  let t63;
  if ($[180] !== page || $[181] !== pagination.total) {
    t63 = buildPaginationRange({
      page,
      limit: 10,
      total: pagination.total
    });
    $[180] = page;
    $[181] = pagination.total;
    $[182] = t63;
  } else {
    t63 = $[182];
  }
  const paginationRange = t63;
  let t64;
  bb1: {
    if (activeTab === "ALL") {
      t64 = "Resultados";
      break bb1;
    }
    t64 = `Resultados (${tabLabelMap[activeTab]})`;
  }
  const resultsLabel = t64;
  let t65;
  if ($[183] !== filteredAndSortedRows) {
    t65 = filteredAndSortedRows.filter(_temp18);
    $[183] = filteredAndSortedRows;
    $[184] = t65;
  } else {
    t65 = $[184];
  }
  const needsActionCount = t65.length;
  let t66;
  if ($[185] !== filteredAndSortedRows) {
    t66 = filteredAndSortedRows.filter(_temp19);
    $[185] = filteredAndSortedRows;
    $[186] = t66;
  } else {
    t66 = $[186];
  }
  const concludedCount = t66.length;
  const t67 = String(pagination.total);
  const t68 = String(needsActionCount);
  const t69 = String(concludedCount);
  let t70;
  if ($[187] !== resultsLabel || $[188] !== t67 || $[189] !== t68 || $[190] !== t69) {
    t70 = {
      resultsLabel,
      resultsValue: t67,
      needsActionLabel: "Aguardando a\xE7\xE3o",
      needsActionValue: t68,
      overdueLabel: "Em atraso",
      overdueValue: "0",
      concludedLabel: "Conclu\xEDdas",
      concludedValue: t69
    };
    $[187] = resultsLabel;
    $[188] = t67;
    $[189] = t68;
    $[190] = t69;
    $[191] = t70;
  } else {
    t70 = $[191];
  }
  const kpis = t70;
  const t71 = error ?? null;
  let t72;
  if ($[192] !== activeChips || $[193] !== activeTab || $[194] !== aplicarPeriodo || $[195] !== applyFilters || $[196] !== archiveCaso || $[197] !== archiveSelected || $[198] !== atualizarRascunhoPeriodo || $[199] !== campoPeriodoAtivo || $[200] !== cancelStartCaso || $[201] !== clearAllFilters || $[202] !== clearFilterCategory || $[203] !== closeCaso || $[204] !== closeSelected || $[205] !== encerrarCaso || $[206] !== exportCsv || $[207] !== filteredAndSortedRows || $[208] !== filters || $[209] !== handleSort || $[210] !== hasActiveFilters || $[211] !== hasExistingNegociacoes || $[212] !== hasTempFilters || $[213] !== isDrawerOpen || $[214] !== isExportingCsv || $[215] !== isLoading || $[216] !== kpis || $[217] !== legendaPeriodo || $[218] !== maxPage || $[219] !== openDrawer || $[220] !== page || $[221] !== pagination.total || $[222] !== paginationRange.text || $[223] !== pauseCaso || $[224] !== pauseSelected || $[225] !== periodo || $[226] !== removeChip || $[227] !== resumeCaso || $[228] !== resumeSelected || $[229] !== resumoPreviaPeriodo || $[230] !== retryFetch || $[231] !== searchInput || $[232] !== searchQuery || $[233] !== selectedIds || $[234] !== setActiveTab || $[235] !== setFilters || $[236] !== setPage || $[237] !== sortConfig || $[238] !== t71 || $[239] !== tabCounts || $[240] !== tempFilters || $[241] !== tempPeriodo || $[242] !== toggleFilterValue || $[243] !== toggleSelectAll || $[244] !== unarchiveCaso || $[245] !== unarchiveSelected) {
    t72 = {
      activeTab,
      setActiveTab,
      page,
      setPage,
      limit: 10,
      isLoading,
      error: t71,
      totalResults: pagination.total,
      totalPages: maxPage,
      paginationText: paginationRange.text,
      searchInput,
      setSearchInput,
      searchQuery,
      filters,
      setFilters,
      tempFilters,
      setTempFilters,
      isDrawerOpen,
      setIsDrawerOpen,
      hasActiveFilters,
      hasTempFilters,
      periodo,
      campoPeriodoAtivo,
      legendaPeriodo,
      aplicarPeriodo,
      atualizarRascunhoPeriodo,
      resumoPreviaPeriodo,
      tempPeriodo,
      setTempPeriodo,
      activeChips,
      sortConfig,
      handleSort,
      selectedIds,
      toggleSelectAll,
      toggleSelect,
      clearSelection,
      pauseCaso,
      resumeCaso,
      closeCaso,
      encerrarCaso,
      archiveCaso,
      unarchiveCaso,
      cancelStartCaso,
      pauseSelected,
      resumeSelected,
      archiveSelected,
      unarchiveSelected,
      closeSelected,
      rows: filteredAndSortedRows,
      openDrawer,
      applyFilters,
      clearTempFilters,
      clearAllFilters,
      removeChip,
      toggleFilterValue,
      clearFilterCategory,
      toggleTempFilterValue,
      selectAllTempInCategory,
      retryFetch,
      exportCsv,
      isExportingCsv,
      kpis,
      hasExistingNegociacoes,
      tabCounts
    };
    $[192] = activeChips;
    $[193] = activeTab;
    $[194] = aplicarPeriodo;
    $[195] = applyFilters;
    $[196] = archiveCaso;
    $[197] = archiveSelected;
    $[198] = atualizarRascunhoPeriodo;
    $[199] = campoPeriodoAtivo;
    $[200] = cancelStartCaso;
    $[201] = clearAllFilters;
    $[202] = clearFilterCategory;
    $[203] = closeCaso;
    $[204] = closeSelected;
    $[205] = encerrarCaso;
    $[206] = exportCsv;
    $[207] = filteredAndSortedRows;
    $[208] = filters;
    $[209] = handleSort;
    $[210] = hasActiveFilters;
    $[211] = hasExistingNegociacoes;
    $[212] = hasTempFilters;
    $[213] = isDrawerOpen;
    $[214] = isExportingCsv;
    $[215] = isLoading;
    $[216] = kpis;
    $[217] = legendaPeriodo;
    $[218] = maxPage;
    $[219] = openDrawer;
    $[220] = page;
    $[221] = pagination.total;
    $[222] = paginationRange.text;
    $[223] = pauseCaso;
    $[224] = pauseSelected;
    $[225] = periodo;
    $[226] = removeChip;
    $[227] = resumeCaso;
    $[228] = resumeSelected;
    $[229] = resumoPreviaPeriodo;
    $[230] = retryFetch;
    $[231] = searchInput;
    $[232] = searchQuery;
    $[233] = selectedIds;
    $[234] = setActiveTab;
    $[235] = setFilters;
    $[236] = setPage;
    $[237] = sortConfig;
    $[238] = t71;
    $[239] = tabCounts;
    $[240] = tempFilters;
    $[241] = tempPeriodo;
    $[242] = toggleFilterValue;
    $[243] = toggleSelectAll;
    $[244] = unarchiveCaso;
    $[245] = unarchiveSelected;
    $[246] = t72;
  } else {
    t72 = $[246];
  }
  return t72;
};
function _temp() {
  if (typeof window === "undefined") {
    return false;
  }
  return window.localStorage.getItem("hasExistingNegociacoes") === "true";
}
function _temp2(prev) {
  if (prev.status.length === 0) {
    return prev;
  }
  return {
    ...prev,
    status: []
  };
}
function _temp3(v) {
  return v.length > 0;
}
function _temp4(v_0) {
  return v_0.length > 0;
}
function _temp5(n) {
  return n.id;
}
function _temp6(n_1) {
  return n_1.statusValue !== "PAUSED" && n_1.statusValue !== "CLOSED" && n_1.statusValue !== "AGREEMENT_CLOSED" && n_1.statusValue !== "NO_AGREEMENT" && !n_1.isArchived;
}
function _temp7(n_2) {
  return n_2.id;
}
function _temp8(r) {
  return pauseNegociacaoCaso.fulfilled.match(r);
}
function _temp9(n_4) {
  return n_4.statusValue === "PAUSED";
}
function _temp10(n_5) {
  return n_5.id;
}
function _temp11(r_0) {
  return resumeNegociacaoCaso.fulfilled.match(r_0);
}
function _temp12(n_7) {
  return n_7.id;
}
function _temp13(r_1) {
  return archiveNegociacaoCaso.fulfilled.match(r_1);
}
function _temp14(n_9) {
  return n_9.id;
}
function _temp15(r_2) {
  return unarchiveNegociacaoCaso.fulfilled.match(r_2);
}
function _temp16(n_11) {
  return n_11.statusValue !== "CLOSED" && n_11.statusValue !== "AGREEMENT_CLOSED" && n_11.statusValue !== "NO_AGREEMENT" && !n_11.isArchived;
}
function _temp17(n_12) {
  return n_12.id;
}
function _temp18(n_13) {
  return n_13.needsAction;
}
function _temp19(n_14) {
  return n_14.statusValue === "AGREEMENT_CLOSED" || n_14.statusValue === "NO_AGREEMENT";
}